import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchGallery, type GalleryImage } from '@/api/client';
import { galleryImages as mockGalleryImages } from '@/mocks/gallery';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function Gallery() {
  const { t } = useTranslation('home');
  const [galleryList, setGalleryList] = useState<GalleryImage[]>(mockGalleryImages);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchGallery().then((data) => {
      if (cancelled) return;
      if (data.length === 0) {
        setGalleryList(mockGalleryImages);
        return;
      }
      // fetchGallery() already maps: image_url→src, alt_text→alt
      const merged = data.map((apiImg, i) => {
        const fallback = mockGalleryImages[i] || mockGalleryImages[0];
        return {
          ...fallback,
          id: apiImg.id,
          src: apiImg.src?.trim() || fallback.src,
          alt: apiImg.alt || apiImg.title || fallback.alt,
          title: apiImg.title || fallback.title,
          category: apiImg.category || fallback.category,
        };
      });
      setGalleryList(merged);
    }).catch(() => {
      if (!cancelled) setGalleryList(mockGalleryImages);
    });
    return () => { cancelled = true; };
  }, []);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const nextImage = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % galleryList.length);
  }, [lightboxIndex, galleryList.length]);

  const prevImage = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + galleryList.length) % galleryList.length);
  }, [lightboxIndex, galleryList.length]);

  return (
    <section id="gallery" className="w-full py-16 md:py-20 bg-white">
      <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto">
        {/* Section Header */}
        <ScrollReveal className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-brand-mint" />
              <span className="text-sm font-medium tracking-wider uppercase text-brand-mint">
                {t('gallery_label')}
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-brand-dark font-heading leading-tight mb-4">
              {t('gallery_title')}
            </h2>
            <div className="w-10 h-1 bg-brand-mint rounded-full" />
          </div>
        </ScrollReveal>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {galleryList.map((img, idx) => (
            <ScrollReveal key={img.id} delay={idx * 80}>
              <div
                className="group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer bg-brand-light"
                onClick={() => openLightbox(idx)}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-brand-dark/0 group-hover:bg-brand-dark/40 transition-colors duration-300 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-zoom-in-line text-brand-dark text-lg" />
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && galleryList[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white cursor-pointer z-10"
            aria-label={t('admin_close_lightbox')}
          >
            <i className="ri-close-line text-xl" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white cursor-pointer z-10"
            aria-label={t('admin_prev_image')}
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white cursor-pointer z-10"
            aria-label={t('admin_next_image')}
          >
            <i className="ri-arrow-right-line text-xl" />
          </button>

          <div
            className="max-w-4xl max-h-[80vh] rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryList[lightboxIndex].src}
              alt={galleryList[lightboxIndex].alt}
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      )}
    </section>
  );
}